<template>
  <div class="product-detail-page">
    <!-- 返回按钮 -->
    <div class="back-nav">
      <div class="container">
        <button class="back-btn" @click="goBack">
          <span>&#8249;</span> {{ t.productDetail.backToList }}
        </button>
        <div class="language-switch">
          <button 
            :class="{ active: locale === 'zh' }" 
            @click="switchLanguage('zh')"
          >{{ t.language.zh }}</button>
          <span>|</span>
          <button 
            :class="{ active: locale === 'en' }" 
            @click="switchLanguage('en')"
          >{{ t.language.en }}</button>
        </div>
      </div>
    </div>

    <!-- 顶部图片轮播 -->
    <section class="product-gallery">
      <div class="container">
        <div class="gallery-wrapper">
          <div class="main-image">
            <img :src="currentImage" :alt="product.name" @click="openLightbox(currentImageIndex)" v-if="currentImage">
            <div class="image-placeholder" v-else>{{ t.products.noImage }}</div>
            <button class="nav-btn prev" @click="prevImage" v-if="processedImages.length > 1">
              <span>&#8249;</span>
            </button>
            <button class="nav-btn next" @click="nextImage" v-if="processedImages.length > 1">
              <span>&#8250;</span>
            </button>
            <div class="image-counter" v-if="processedImages.length > 0">{{ currentImageIndex + 1 }} / {{ processedImages.length }}</div>
          </div>
          <div class="thumbnail-list" v-if="processedImages.length > 0">
            <div 
              v-for="(img, index) in processedImages" 
              :key="index"
              class="thumbnail"
              :class="{ active: currentImageIndex === index }"
              @click="selectImage(index)"
            >
              <img :src="img" :alt="`${product.name} - ${index + 1}`">
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 产品信息和其他图片 -->
    <section class="product-info-section">
      <div class="container">
        <div class="product-header">
          <span class="product-model">{{ product.type }}</span>
          <h1 class="product-name">{{ product.name }}</h1>
        </div>
        
        <div class="product-description">
          <h2>{{ t.productDetail.features }}</h2>
          <div class="feature-list" v-if="product.description">
            <template v-for="(desc, index) in processedDescription">
              <div v-if="desc.type === 'heading'" :key="'h-' + index" class="feature-heading">
                <h3>{{ desc.text }}</h3>
              </div>
              <div v-else :key="'i-' + index" class="feature-item">
                <span class="feature-number">{{ desc.number }}</span>
                <p class="feature-text">{{ desc.text }}</p>
              </div>
            </template>
          </div>
        </div>

        <!-- 其他产品图片 - 横向排列在产品描述下方 -->
        <div class="other-images-row" v-if="processedOtherImages.length > 0">
          <div 
            v-for="(img, index) in processedOtherImages" 
            :key="index"
            class="other-image-strip"
            @click="openLightbox(index, true)"
          >
            <img :src="img" :alt="`${product.name} - 其他 ${index + 1}`">
          </div>
        </div>
      </div>
    </section>

    <!-- 固定浮动邮件按钮 -->
    <!-- <a href="mailto:sales@sunsure-cn.com" class="floating-email-btn" :title="t.productDetail.contactUs">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
        <polyline points="22,6 12,13 2,6"></polyline>
      </svg>
      <span class="btn-text">{{ t.productDetail.emailConsult }}</span>
    </a> -->

    <!-- 图片灯箱 -->
    <div class="lightbox" v-if="lightboxOpen" @click="closeLightbox">
      <button class="lightbox-close" @click="closeLightbox">&times;</button>
      <button class="lightbox-nav prev" @click.stop="lightboxPrev" v-if="lightboxImages.length > 1">
        <span>&#8249;</span>
      </button>
      <img :src="lightboxImages[lightboxIndex]" :alt="product.name">
      <button class="lightbox-nav next" @click.stop="lightboxNext" v-if="lightboxImages.length > 1">
        <span>&#8250;</span>
      </button>
    </div>
  </div>
</template>

<script>
import { messages } from '../i18n/index.js'

export default {
  name: 'ProductDetail',
  props: {
    productId: {
      type: Number,
      default: 1
    },
    locale: {
      type: String,
      default: 'en'
    }
  },
  data() {
    return {
      product: {},
      currentImageIndex: 0,
      lightboxOpen: false,
      lightboxIndex: 0,
      lightboxImages: [],
      isOtherImages: false,
      processedImages: [],
      processedOtherImages: [],
      isLoading: false
    }
  },
  computed: {
    t() {
      return messages[this.locale] || messages.en
    },
    currentImage() {
      if (this.processedImages.length > 0) {
        return this.processedImages[this.currentImageIndex]
      }
      return ''
    },
    processedDescription() {
      if (!this.product.description || !Array.isArray(this.product.description)) return []
      const keywords = [
        'APPLICATION',
        'Applicable',
        'FEATURES',
        'Common Specifications',
        'Functional Characteristics',
        'Characteristics',
        'ADVANTAGES',
        'Machine Configuration Description'
      ]
      const result = []
      let currentNumber = 0
      for (const item of this.product.description) {
        const trimmed = String(item).trim()
        const isHeading = keywords.some(kw => {
          const pattern = new RegExp(`^\\s*\\d*[.]?\\s*${kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`, 'i')
          return pattern.test(trimmed)
        })
        if (isHeading) {
          const text = trimmed.replace(/^\s*\d+[.)\s]*\s*/, '')
          result.push({ type: 'heading', text })
          currentNumber = 0
        } else {
          currentNumber++
          result.push({ type: 'item', number: currentNumber, text: item })
        }
      }
      return result
    }
  },
  watch: {
    productId: {
      immediate: true,
      handler(newId) {
        this.loadProduct(newId)
      }
    }
  },
  methods: {
    // 处理图片路径，将 /assets/ 开头的路径转换为 require
    processImagePath(imgPath) {
      if (!imgPath) return ''
      
      if (imgPath.startsWith('/assets/')) {
        try {
          const relativePath = imgPath.replace('/assets/', '')
          return require(`../assets/${relativePath}`)
        } catch (e) {
          console.warn('图片加载失败:', imgPath, e)
          return imgPath
        }
      }
      return imgPath
    },
    
    // 处理图片数组
    processImages(imageArray) {
      if (!Array.isArray(imageArray)) return []
      return imageArray.map(img => this.processImagePath(img))
    },
    
    async loadProduct(id) {
      this.isLoading = true
      try {
        // 动态导入对应产品数据
        const module = await import(`../assets/machine/${id}/machine_txt.js`)
        const rawProduct = module.machine_txt || module.default?.machine_txt
        
        if (!rawProduct) {
          console.error('产品数据加载失败:', id)
          this.product = {}
          this.processedImages = []
          this.processedOtherImages = []
          return
        }
        
        // 深拷贝产品数据并处理图片路径
        this.product = { ...rawProduct }
        this.processedImages = this.processImages(rawProduct.machine_img || [])
        this.processedOtherImages = this.processImages(rawProduct.machine_other_img || [])
        this.currentImageIndex = 0
      } catch (error) {
        console.error('加载产品数据失败:', id, error)
        this.product = {}
        this.processedImages = []
        this.processedOtherImages = []
      } finally {
        this.isLoading = false
      }
    },
    goBack() {
      this.$emit('back')
    },
    switchLanguage(lang) {
      this.$emit('change-locale', lang)
    },
    selectImage(index) {
      this.currentImageIndex = index
    },
    nextImage() {
      if (this.processedImages.length > 0) {
        this.currentImageIndex = (this.currentImageIndex + 1) % this.processedImages.length
      }
    },
    prevImage() {
      if (this.processedImages.length > 0) {
        this.currentImageIndex = (this.currentImageIndex - 1 + this.processedImages.length) % this.processedImages.length
      }
    },
    openLightbox(index, isOther = false) {
      this.isOtherImages = isOther
      this.lightboxImages = isOther ? this.processedOtherImages : this.processedImages
      this.lightboxIndex = index
      this.lightboxOpen = true
      document.body.style.overflow = 'hidden'
    },
    closeLightbox() {
      this.lightboxOpen = false
      document.body.style.overflow = ''
    },
    lightboxNext() {
      this.lightboxIndex = (this.lightboxIndex + 1) % this.lightboxImages.length
    },
    lightboxPrev() {
      this.lightboxIndex = (this.lightboxIndex - 1 + this.lightboxImages.length) % this.lightboxImages.length
    }
  },
  mounted() {
    // 键盘导航
    document.addEventListener('keydown', (e) => {
      if (!this.lightboxOpen) return
      if (e.key === 'Escape') this.closeLightbox()
      if (e.key === 'ArrowLeft') this.lightboxPrev()
      if (e.key === 'ArrowRight') this.lightboxNext()
    })
  }
}
</script>

<style scoped>
.product-detail-page {
  padding-top: 0;
  min-height: 100vh;
  background: var(--color-background);
}

/* 返回导航 */
.back-nav {
  background: var(--color-surface);
  padding: var(--space-md) 0;
  border-bottom: 1px solid var(--color-border);
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: var(--shadow-sm);
}

.back-nav .container {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.language-switch {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
}

.language-switch button {
  background: none;
  border: none;
  color: var(--color-text-muted);
  cursor: pointer;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  padding: var(--space-xs) var(--space-sm);
  transition: all var(--transition-base);
  border-radius: var(--radius-sm);
}

.language-switch button.active,
.language-switch button:hover {
  color: var(--color-primary);
  background: var(--color-primary-soft);
}

.language-switch span {
  color: var(--color-text-subtle);
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: var(--space-sm);
  background: none;
  border: none;
  color: var(--color-text-muted);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all var(--transition-base);
  padding: var(--space-sm) var(--space-md) var(--space-sm) 0;
  border-radius: var(--radius-sm);
}

.back-btn:hover {
  color: var(--color-primary);
}

.back-btn span {
  font-size: var(--font-size-xl);
  line-height: 1;
  transition: transform var(--transition-base);
}

.back-btn:hover span {
  transform: translateX(-3px);
}

/* 图片画廊 */
.product-gallery {
  padding: var(--space-2xl) 0;
  background: var(--color-surface);
}

.gallery-wrapper {
  max-width: 900px;
  margin: 0 auto;
}

.main-image {
  position: relative;
  background: linear-gradient(135deg, #f4f5f7 0%, #fafbfc 100%);
  border-radius: var(--radius-xl);
  overflow: hidden;
  aspect-ratio: 16/10;
  cursor: zoom-in;
  box-shadow: var(--shadow-md);
  border: 1px solid var(--color-border);
}

.main-image img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: transform var(--transition-slow);
}

.image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-subtle);
  font-size: var(--font-size-md);
  background: var(--color-background);
}

.main-image:hover img {
  transform: scale(1.02);
}

.nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid var(--color-border);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--font-size-xl);
  color: var(--color-text-heading);
  transition: all var(--transition-base);
  box-shadow: var(--shadow-md);
  z-index: 10;
}

.nav-btn:hover {
  background: var(--color-primary);
  color: var(--color-surface);
  border-color: var(--color-primary);
  transform: translateY(-50%) scale(1.05);
}

.nav-btn:active {
  transform: translateY(-50%) scale(0.98);
}

.nav-btn.prev {
  left: var(--space-lg);
}

.nav-btn.next {
  right: var(--space-lg);
}

.image-counter {
  position: absolute;
  bottom: var(--space-md);
  right: var(--space-md);
  background: rgba(0, 0, 0, 0.72);
  color: var(--color-surface);
  padding: var(--space-xs) var(--space-md);
  border-radius: var(--radius-full);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  letter-spacing: var(--letter-spacing-wide);
  backdrop-filter: blur(4px);
}

.thumbnail-list {
  display: flex;
  gap: var(--space-md);
  margin-top: var(--space-lg);
  overflow-x: auto;
  padding-bottom: var(--space-sm);
}

.thumbnail {
  flex-shrink: 0;
  width: 100px;
  height: 70px;
  border-radius: var(--radius-md);
  overflow: hidden;
  cursor: pointer;
  border: 2px solid transparent;
  transition: all var(--transition-base);
  background: var(--color-background);
}

.thumbnail:hover {
  border-color: var(--color-primary);
}

.thumbnail.active {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px var(--color-primary-soft);
}

.thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform var(--transition-slow);
}

.thumbnail:hover img {
  transform: scale(1.05);
}

/* 产品信息 */
.product-info-section {
  padding: var(--space-3xl) 0;
}

.product-header {
  margin-bottom: var(--space-2xl);
  padding-bottom: var(--space-xl);
  border-bottom: 2px solid var(--color-border);
}

.product-model {
  display: inline-block;
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-light) 100%);
  color: var(--color-surface);
  padding: var(--space-xs) var(--space-md);
  border-radius: var(--radius-sm);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  letter-spacing: var(--letter-spacing-wide);
  margin-bottom: var(--space-md);
}

.product-name {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-heading);
  line-height: var(--line-height-tight);
  letter-spacing: var(--letter-spacing-tight);
}

.product-description h2 {
  font-size: var(--font-size-xl);
  color: var(--color-text-heading);
  margin-bottom: var(--space-lg);
  position: relative;
  padding-bottom: var(--space-md);
  font-weight: var(--font-weight-bold);
  letter-spacing: var(--letter-spacing-tight);
}

.product-description h2::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 50px;
  height: 3px;
  background: linear-gradient(90deg, var(--color-primary), var(--color-primary-light));
  border-radius: var(--radius-full);
}

/* 产品特点列表 */
.feature-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
}

.feature-heading {
  margin-top: var(--space-sm);
  padding-bottom: var(--space-xs);
  border-bottom: 1px solid var(--color-border);
}

.feature-heading h3 {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-heading);
  text-transform: uppercase;
  letter-spacing: var(--letter-spacing-wide);
  margin: 0;
}

.feature-item {
  display: flex;
  align-items: flex-start;
  gap: var(--space-md);
  padding: var(--space-md) var(--space-lg);
  background: var(--color-surface);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  transition: all var(--transition-base);
  border: 1px solid var(--color-border);
}

.feature-item:hover {
  transform: translateX(5px);
  box-shadow: var(--shadow-md);
  border-color: var(--color-border-hover);
}

.feature-number {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-light) 100%);
  color: var(--color-surface);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-base);
  box-shadow: var(--shadow-primary);
}

.feature-text {
  flex: 1;
  font-size: var(--font-size-base);
  line-height: var(--line-height-normal);
  color: var(--color-text);
  margin: 0;
}

/* 其他图片 */
.other-images-row {
  display: flex;
  flex-direction: column;
  gap: var(--space-lg);
  margin-top: var(--space-2xl);
}

.other-image-strip {
  width: 100%;
  border-radius: var(--radius-xl);
  overflow: hidden;
  cursor: zoom-in;
  background: var(--color-background);
  transition: all var(--transition-base);
  border: 1px solid var(--color-border);
}

.other-image-strip:hover {
  box-shadow: var(--shadow-lg);
  border-color: var(--color-border-hover);
}

.other-image-strip img {
  width: 100%;
  object-fit: cover;
  transition: transform var(--transition-slow);
}

.other-image-strip:hover img {
  transform: scale(1.03);
}

/* 灯箱 */
.lightbox {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: var(--space-2xl);
}

.lightbox img {
  max-width: 90%;
  max-height: 90%;
  object-fit: contain;
}

.lightbox-close {
  position: absolute;
  top: var(--space-md);
  right: var(--space-xl);
  background: none;
  border: none;
  color: var(--color-surface);
  font-size: var(--font-size-3xl);
  cursor: pointer;
  transition: color var(--transition-base);
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.lightbox-close:hover {
  color: var(--color-primary-light);
}

.lightbox-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: var(--color-surface);
  font-size: var(--font-size-2xl);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all var(--transition-base);
}

.lightbox-nav:hover {
  background: var(--color-primary);
  border-color: var(--color-primary);
}

.lightbox-nav:active {
  transform: translateY(-50%) scale(0.96);
}

.lightbox-nav.prev {
  left: var(--space-xl);
}

.lightbox-nav.next {
  right: var(--space-xl);
}

/* 响应式 */
@media (max-width: 768px) {
  .product-detail-page {
    padding-top: 0;
  }

  .back-nav {
    padding: var(--space-sm) 0;
    top: 0;
  }

  .product-gallery {
    padding: var(--space-lg) 0;
  }

  .main-image {
    aspect-ratio: 4/3;
  }

  .nav-btn {
    width: 42px;
    height: 42px;
    font-size: var(--font-size-lg);
  }

  .nav-btn.prev {
    left: var(--space-sm);
  }

  .nav-btn.next {
    right: var(--space-sm);
  }

  .thumbnail {
    width: 80px;
    height: 55px;
  }

  .product-name {
    font-size: var(--font-size-xl);
  }

  .product-info-section {
    padding: var(--space-2xl) 0;
  }

  .feature-item {
    padding: var(--space-md);
    gap: var(--space-md);
  }

  .feature-number {
    width: 30px;
    height: 30px;
    font-size: var(--font-size-sm);
  }

  .feature-text {
    font-size: var(--font-size-sm);
  }

  .other-images-row {
    gap: var(--space-md);
    margin-top: var(--space-xl);
  }

  .lightbox {
    padding: var(--space-md);
  }

  .lightbox-nav {
    width: 46px;
    height: 46px;
    font-size: var(--font-size-xl);
  }

  .lightbox-nav.prev {
    left: var(--space-sm);
  }

  .lightbox-nav.next {
    right: var(--space-sm);
  }

  .lightbox-close {
    right: var(--space-md);
    font-size: var(--font-size-2xl);
  }
}
</style>